import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SummarizeIcon from '@mui/icons-material/Summarize';
import RestoreIcon from '@mui/icons-material/Restore';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import {
  cleanupTranscript,
  TranscriptCleanupMode,
} from '../../actions/transcript/cleanupTranscript';
import MicrophonePermissionModal from './MicrophonePermissionModal';
import {
  DICTATION_LANGUAGES,
  DICTATION_LANG_STORAGE_KEY,
  findDictationLanguage,
} from '../../constants/dictationLanguages';
import { shouldShowVoiceTranscribe } from '../../utils/env-utils';

async function requestMicrophonePermission(): Promise<
  { ok: true } | { ok: false; reason: 'denied' | 'unsupported' | 'other'; error?: string }
> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getUserMedia !== 'function'
  ) {
    return { ok: false, reason: 'unsupported' };
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return { ok: true };
  } catch (err: any) {
    const name = err?.name || '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      return { ok: false, reason: 'denied', error: err?.message };
    }
    return { ok: false, reason: 'other', error: err?.message };
  }
}

type InheritedProps = Omit<TextFieldProps, 'onChange' | 'value'>;

export interface ContextDictationFieldProps extends InheritedProps {
  value: string;
  onChange: (value: string) => void;
  /** Disable clean/summarise chips even when a transcript is present. */
  cleanupEnabled?: boolean;
  /** Locale for Web Speech. Defaults to en-ZA with browser fallback. */
  lang?: string;
}

type ViewMode = 'raw' | 'clean' | 'summarise';

const PlainContextField: React.FC<ContextDictationFieldProps> = ({
  value,
  onChange,
  cleanupEnabled: _cleanupEnabled,
  lang: _lang,
  disabled,
  multiline = true,
  minRows = 3,
  ...textFieldProps
}) => (
  <TextField
    {...textFieldProps}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    multiline={multiline}
    minRows={minRows}
    fullWidth
  />
);

const VoiceDictationField: React.FC<ContextDictationFieldProps> = ({
  value,
  onChange,
  cleanupEnabled = true,
  lang = 'en-ZA',
  disabled,
  multiline = true,
  minRows = 3,
  ...textFieldProps
}) => {
  const [selectedLang, setSelectedLang] = useState(() => {
    if (typeof window === 'undefined') return findDictationLanguage(lang);
    const stored = window.localStorage.getItem(DICTATION_LANG_STORAGE_KEY);
    return findDictationLanguage(stored ?? lang);
  });
  const [langMenuAnchor, setLangMenuAnchor] = useState<HTMLElement | null>(null);

  const {
    isSupported,
    isListening,
    interimText,
    finalText,
    error,
    start,
    stop,
  } = useSpeechRecognition({ lang: selectedLang.code });

  const baseValueRef = useRef<string>(value);
  const rawDictatedRef = useRef<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>('raw');
  const [cleanupLoading, setCleanupLoading] = useState<TranscriptCleanupMode | null>(
    null,
  );
  const [cleanupError, setCleanupError] = useState<string | null>(null);
  const [hasTranscript, setHasTranscript] = useState(false);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);

  const handleSelectLang = useCallback((code: string) => {
    const next = findDictationLanguage(code);
    setSelectedLang(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DICTATION_LANG_STORAGE_KEY, next.code);
    }
    setLangMenuAnchor(null);
  }, []);

  // When dictation starts, remember the field's existing text so we can append.
  const handleStart = useCallback(async () => {
    baseValueRef.current = value;
    rawDictatedRef.current = '';
    setHasTranscript(false);
    setViewMode('raw');
    setCleanupError(null);

    // Warm the mic via getUserMedia first — this reliably triggers Chrome's
    // permission prompt and avoids the Web Speech API's silent `not-allowed`
    // failure mode where SpeechRecognition.start() rejects before the browser
    // has had a chance to ask the user.
    const result = await requestMicrophonePermission();
    if (!result.ok) {
      if (result.reason === 'denied') {
        setPermissionModalOpen(true);
        return;
      }
      // Unsupported or other error — fall through to start() so the hook's
      // error message surfaces the underlying cause.
    }
    start();
  }, [start, value]);

  const handlePermissionRetry = useCallback(() => {
    setPermissionModalOpen(false);
    void handleStart();
  }, [handleStart]);

  // Stream transcript into the parent-owned value while listening.
  useEffect(() => {
    if (!isListening && !finalText && !interimText) return;
    const dictated = [finalText, interimText].filter(Boolean).join(' ').trim();
    rawDictatedRef.current = finalText.trim();
    const composed = [baseValueRef.current, dictated].filter(Boolean).join('\n\n');
    onChange(composed);
  }, [finalText, interimText, isListening, onChange]);

  // When listening ends and we have a final transcript, surface the chip row.
  useEffect(() => {
    if (!isListening && rawDictatedRef.current.length > 0) {
      setHasTranscript(true);
    }
  }, [isListening]);

  // If SpeechRecognition still rejects with `not-allowed` even after getUserMedia
  // succeeded (Chrome sometimes caches a stale denial against the speech API
  // specifically), open the modal so the user has an actionable next step.
  useEffect(() => {
    if (error === 'not-allowed' || error === 'service-not-allowed') {
      setPermissionModalOpen(true);
    }
  }, [error]);

  const handleStop = useCallback(() => {
    stop();
  }, [stop]);

  const applyTranscform = useCallback(
    async (mode: TranscriptCleanupMode) => {
      const source = rawDictatedRef.current;
      if (!source) return;
      setCleanupLoading(mode);
      setCleanupError(null);
      try {
        const cleaned = await cleanupTranscript(source, mode);
        const composed = [baseValueRef.current, cleaned].filter(Boolean).join('\n\n');
        onChange(composed);
        setViewMode(mode);
      } catch (err: any) {
        setCleanupError(
          err?.response?.data?.message || err?.message || 'Cleanup failed',
        );
      } finally {
        setCleanupLoading(null);
      }
    },
    [onChange],
  );

  const restoreRaw = useCallback(() => {
    const composed = [baseValueRef.current, rawDictatedRef.current]
      .filter(Boolean)
      .join('\n\n');
    onChange(composed);
    setViewMode('raw');
    setCleanupError(null);
  }, [onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(e.target.value);
    // Manual edits reset the cleanup baseline.
    if (hasTranscript && viewMode !== 'raw') {
      setViewMode('raw');
    }
  };

  const isBusy = disabled || isListening || cleanupLoading !== null;

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        multiline={multiline}
        minRows={minRows}
        fullWidth
        InputProps={{
          ...(textFieldProps.InputProps || {}),
          sx: {
            pr: isSupported ? 6 : undefined,
            ...((textFieldProps.InputProps as any)?.sx || {}),
          },
        }}
      />

      {isSupported && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {isListening && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'var(--err)',
                animation: 'pulse 1.2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 },
                },
              }}
            />
          )}
          <Tooltip title={`Dictation language: ${selectedLang.label}`}>
            <span>
              <Button
                size="small"
                aria-label="Select dictation language"
                onClick={(e) => setLangMenuAnchor(e.currentTarget)}
                disabled={disabled || isListening || cleanupLoading !== null}
                endIcon={<ArrowDropDownIcon sx={{ fontSize: 16, ml: -0.5 }} />}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0.25,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  lineHeight: 1.4,
                  color: 'var(--t2)',
                  bgcolor: 'var(--s1)',
                  borderRadius: '6px',
                  '&:hover': { bgcolor: 'var(--s2)' },
                  '&.Mui-disabled': { color: 'var(--t4)' },
                }}
              >
                {selectedLang.shortLabel}
              </Button>
            </span>
          </Tooltip>
          <Menu
            anchorEl={langMenuAnchor}
            open={Boolean(langMenuAnchor)}
            onClose={() => setLangMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {DICTATION_LANGUAGES.map((opt) => (
              <MenuItem
                key={opt.code}
                selected={opt.code === selectedLang.code}
                onClick={() => handleSelectLang(opt.code)}
                sx={{ fontSize: '0.85rem', minHeight: 32 }}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Menu>
          <Tooltip title={isListening ? 'Stop dictation' : 'Start dictation'}>
            <span>
              <IconButton
                size="small"
                aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
                onClick={isListening ? handleStop : handleStart}
                disabled={disabled || cleanupLoading !== null}
                sx={{
                  bgcolor: isListening ? 'rgba(217,48,37,0.12)' : 'var(--s1)',
                  '&:hover': {
                    bgcolor: isListening ? 'rgba(217,48,37,0.22)' : 'var(--s2)',
                  },
                }}
              >
                {isListening ? (
                  <StopIcon fontSize="small" sx={{ color: 'var(--err)' }} />
                ) : (
                  <MicIcon fontSize="small" sx={{ color: 'var(--t2)' }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}

      {cleanupEnabled && hasTranscript && !isListening && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}
        >
          <Typography variant="caption" sx={{ color: 'var(--t3)', mr: 0.5 }}>
            Transcript:
          </Typography>
          <Chip
            size="small"
            icon={<RestoreIcon sx={{ fontSize: 16 }} />}
            label="Raw"
            onClick={restoreRaw}
            disabled={isBusy}
            sx={{
              borderRadius: '6px',
              bgcolor: viewMode === 'raw' ? 'var(--teal-dim)' : 'var(--s1)',
              color: viewMode === 'raw' ? 'var(--teal)' : 'var(--t2)',
              '&:hover': { bgcolor: 'var(--s2)' },
            }}
          />
          <Chip
            size="small"
            icon={
              cleanupLoading === 'clean' ? (
                <CircularProgress size={12} />
              ) : (
                <AutoFixHighIcon sx={{ fontSize: 16 }} />
              )
            }
            label="Clean up"
            onClick={() => applyTranscform('clean')}
            disabled={isBusy}
            sx={{
              borderRadius: '6px',
              bgcolor: viewMode === 'clean' ? 'var(--teal-dim)' : 'var(--s1)',
              color: viewMode === 'clean' ? 'var(--teal)' : 'var(--t2)',
              '&:hover': { bgcolor: 'var(--s2)' },
            }}
          />
          <Chip
            size="small"
            icon={
              cleanupLoading === 'summarise' ? (
                <CircularProgress size={12} />
              ) : (
                <SummarizeIcon sx={{ fontSize: 16 }} />
              )
            }
            label="Summarise"
            onClick={() => applyTranscform('summarise')}
            disabled={isBusy}
            sx={{
              borderRadius: '6px',
              bgcolor: viewMode === 'summarise' ? 'var(--teal-dim)' : 'var(--s1)',
              color: viewMode === 'summarise' ? 'var(--teal)' : 'var(--t2)',
              '&:hover': { bgcolor: 'var(--s2)' },
            }}
          />
          {cleanupError && (
            <Typography variant="caption" sx={{ color: 'var(--err)' }}>
              {cleanupError}
            </Typography>
          )}
        </Stack>
      )}

      {error && error !== 'not-allowed' && error !== 'service-not-allowed' && (
        <Typography variant="caption" sx={{ color: 'var(--err)', mt: 0.5, display: 'block' }}>
          Dictation error: {error}
        </Typography>
      )}

      <MicrophonePermissionModal
        open={permissionModalOpen}
        onClose={() => setPermissionModalOpen(false)}
        onRetry={handlePermissionRetry}
      />
    </Box>
  );
};

export const ContextDictationField: React.FC<ContextDictationFieldProps> = (props) => {
  if (!shouldShowVoiceTranscribe()) {
    return <PlainContextField {...props} />;
  }
  return <VoiceDictationField {...props} />;
};

export default ContextDictationField;
